import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import CompactTableSearch from './CompactTableSearch';

function getOptionDefaultOrder(option) {
  if (typeof option.defaultOrder === 'number') {
    return option.defaultOrder;
  }
  return option.value === 'createdAt' ? -1 : 1;
}

function SegmentedControl({ options, value, onChange, allowDeselect = false }) {
  if (!options.length) {
    return null;
  }

  return (
    <div className="table-toolbar__segmented">
      {options.map((option) => {
        const isSelected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            className={classNames('table-toolbar__segment', {
              'table-toolbar__segment--selected': isSelected,
            })}
            onClick={() => {
              if (allowDeselect && isSelected) {
                onChange(null);
                return;
              }
              onChange(option.value);
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function CompactSort({ sortField, sortOrder, sortOptions, onSortChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef(null);
const currentOption =
  sortOptions.find((option) => option.value === sortField) || sortOptions[0];
const defaultSortField = sortOptions[0]?.value;
const isNonDefaultSort =
  defaultSortField != null && sortField !== defaultSortField;

useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') {
        return;
      }
      event.preventDefault();
      setIsOpen(false);
    };

    const handleMouseDown = (event) => {
      if (!wrapRef.current || wrapRef.current.contains(event.target)) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isOpen]);

  if (!sortOptions.length) {
    return null;
  }

  const arrow = sortOrder === 1 ? '↑' : '↓';

  const handleSelectField = (option) => {
    const nextOrder =
      option.value === sortField
        ? sortOrder
        : getOptionDefaultOrder(option);
    onSortChange({ field: option.value, order: nextOrder });
    setIsOpen(false);
  };

  const handleToggleOrder = (event) => {
    event.stopPropagation();
    onSortChange({
      field: sortField,
      order: sortOrder === 1 ? -1 : 1,
    });
  };

  return (
    <div className="table-toolbar__menu-wrap" ref={wrapRef}>
      <button
        type="button"
        className={classNames('table-toolbar__trigger', {
          'table-toolbar__trigger--active': isOpen,
        })}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        onClick={() => setIsOpen((open) => !open)}
      >
<span
  className={classNames('table-toolbar__sort-label', {
    'table-toolbar__sort-label--non-default': isNonDefaultSort,
  })}
>
  {currentOption.label}
</span>
        <span
          className="table-toolbar__sort-arrow"
          onClick={handleToggleOrder}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              handleToggleOrder(event);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Reverse sort direction"
        >
          {arrow}
        </span>
      </button>
      {isOpen ? (
        <div className="table-toolbar__panel" role="listbox">
          {sortOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={classNames('table-toolbar__option', {
                'table-toolbar__option--selected': option.value === sortField,
              })}
              onClick={() => handleSelectField(option)}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function CompactFilter({
  filters,
  filterCategories,
  filterSearchThreshold,
  onFilterChange,
  onClearAllFilters,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [activeCategoryKey, setActiveCategoryKey] = useState(null);
  const [categorySearch, setCategorySearch] = useState('');
  const [frozenChoices, setFrozenChoices] = useState([]);
  const wrapRef = useRef(null);

  const filterCount = useMemo(
    () =>
      filterCategories.reduce((total, category) => {
        const selected = filters[category.key] || [];
        return total + selected.length;
      }, 0),
    [filterCategories, filters],
  );

  const activeCategory = filterCategories.find(
    (category) => category.key === activeCategoryKey,
  );

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') {
        return;
      }
      event.preventDefault();
      if (activeCategoryKey) {
        setActiveCategoryKey(null);
        setCategorySearch('');
        setFrozenChoices([]);
        return;
      }
      setIsOpen(false);
    };

    const handleMouseDown = (event) => {
      if (!wrapRef.current || wrapRef.current.contains(event.target)) {
        return;
      }
      setIsOpen(false);
      setActiveCategoryKey(null);
      setCategorySearch('');
      setFrozenChoices([]);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isOpen, activeCategoryKey]);

  useEffect(() => {
    if (!activeCategory) {
      return;
    }
    if (frozenChoices.length === 0 && activeCategory.choices?.length) {
      setFrozenChoices(activeCategory.choices);
    }
  }, [activeCategory, frozenChoices.length]);

  if (!filterCategories.length) {
    return null;
  }

const summaryItems = filterCategories
  .map((category) => {
    const selected = filters[category.key] || [];
    if (!selected.length) {
      return null;
    }
const choices = category.choices || [];
const labels = [
  ...choices
    .filter((choice) => selected.includes(choice.value))
    .map((choice) => choice.label),
  ...selected
    .filter(
      (value) => !choices.some((choice) => choice.value === value),
    )
    .map((value) => value),
];
    return {
      key: category.key,
      label: category.label,
      valueText: labels.join(', '),
    };
  })
  .filter(Boolean);

  const openCategory = (category) => {
    setActiveCategoryKey(category.key);
    setCategorySearch('');
    setFrozenChoices(category.choices || []);
  };

  const closePanel = () => {
    setIsOpen(false);
    setActiveCategoryKey(null);
    setCategorySearch('');
    setFrozenChoices([]);
  };

  const goBack = () => {
    setActiveCategoryKey(null);
    setCategorySearch('');
    setFrozenChoices([]);
  };

  const selectedForActive = activeCategory
    ? filters[activeCategory.key] || []
    : [];

  const visibleChoices = frozenChoices.filter((choice) => {
    const query = categorySearch.trim().toLowerCase();
    if (!query) {
      return true;
    }
    return (choice.label || '').toLowerCase().includes(query);
  });

  const showCategorySearch =
    frozenChoices.length >=
    (activeCategory?.searchThreshold || filterSearchThreshold);

  const toggleChoice = (choiceValue) => {
    if (!activeCategory) {
      return;
    }
    const selected = filters[activeCategory.key] || [];
    const nextSelected = selected.includes(choiceValue)
      ? selected.filter((value) => value !== choiceValue)
      : [...selected, choiceValue];
    onFilterChange(activeCategory.key, nextSelected);
  };

  return (
    <div
      className="table-toolbar__menu-wrap"
      ref={wrapRef}
      onMouseEnter={() => {
        if (filterCount > 0 && !isOpen) {
          setShowSummary(true);
        }
      }}
      onMouseLeave={() => setShowSummary(false)}
    >
      <button
        type="button"
        className={classNames('table-toolbar__trigger', {
          'table-toolbar__trigger--active': isOpen || filterCount > 0,
        })}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={() => {
          if (isOpen) {
            closePanel();
            return;
          }
          setShowSummary(false);
          setIsOpen(true);
        }}
      >
        {filterCount > 0 ? `Filter · ${filterCount}` : 'Filter'}
      </button>
{showSummary && !isOpen && summaryItems.length ? (
  <div className="table-toolbar__summary">
    {summaryItems.map((item) => (
      <div key={item.key}>
        <strong>{item.label}:</strong> {item.valueText}
      </div>
    ))}
  </div>
) : null}
      {isOpen ? (
        <div className="table-toolbar__panel table-toolbar__panel--right">
          {activeCategory ? (
            <>
              <div className="table-toolbar__panel-header">
                <button
                  type="button"
                  className="table-toolbar__panel-back"
                  onClick={goBack}
                >
                  ‹ {activeCategory.label}
                </button>
                {selectedForActive.length > 0 ? (
                  <button
                    type="button"
                    className="table-toolbar__panel-back"
                    onClick={() => onFilterChange(activeCategory.key, [])}
                  >
                    Clear
                  </button>
                ) : null}
              </div>
              {showCategorySearch ? (
                <div className="table-toolbar__category-search">
                  <input
                    type="text"
                    className="table-toolbar__category-search-input"
                    value={categorySearch}
                    onChange={(event) => setCategorySearch(event.target.value)}
                    placeholder="Search"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck="false"
                  />
                </div>
              ) : null}
              {visibleChoices.map((choice) => (
                <label
                  key={choice.value}
                  className="table-toolbar__choice"
                >
                  <input
                    type="checkbox"
                    checked={selectedForActive.includes(choice.value)}
                    onChange={() => toggleChoice(choice.value)}
                  />
                  {choice.label}
                </label>
              ))}
            </>
          ) : (
            <>
              {filterCategories.map((category) => {
                const selectedCount = (filters[category.key] || []).length;
                return (
                  <button
                    key={category.key}
                    type="button"
                    className="table-toolbar__category"
                    onClick={() => openCategory(category)}
                  >
                    <span>{category.label}</span>
                    <span className="table-toolbar__category-meta">
                      {selectedCount} ›
                    </span>
                  </button>
                );
              })}
              <button
                type="button"
                className="table-toolbar__panel-action"
                onClick={onClearAllFilters}
              >
                Clear all
              </button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function StandardTableToolbar({
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search',
  searchAutofocus = false,
  scope,
  onScopeChange,
  scopeOptions = [],
  sortField,
  sortOrder,
  onSortChange,
  sortOptions = [],
  filters = {},
  onFilterChange,
  onClearAllFilters,
  filterCategories = [],
  filterSearchThreshold = 8,
  timeFilter,
  onTimeFilterChange,
  dateShortcuts = [],
}) {
  return (
    <div className="table-toolbar">
      <CompactTableSearch
        value={searchValue}
        onChange={onSearchChange}
        placeholder={searchPlaceholder}
        autofocus={searchAutofocus}
      />
      <SegmentedControl
        options={scopeOptions}
        value={scope}
        onChange={onScopeChange}
      />
      <CompactSort
        sortField={sortField}
        sortOrder={sortOrder}
        sortOptions={sortOptions}
        onSortChange={onSortChange}
      />
      <CompactFilter
        filters={filters}
        filterCategories={filterCategories}
        filterSearchThreshold={filterSearchThreshold}
        onFilterChange={onFilterChange}
        onClearAllFilters={onClearAllFilters}
      />
      <SegmentedControl
        options={dateShortcuts}
        value={timeFilter}
        onChange={onTimeFilterChange}
        allowDeselect
      />
    </div>
  );
}
